import { Injectable, Injector } from '@angular/core';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { DocumentModel } from './document.model';
import { AppTextService } from '@fi-shared/app-text';
import { DocumentControlContentLanguageLabelModel } from './document-control-content-language-label.model';
import { isObject } from 'util';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ModuleType } from '@fi-shared/types/module-type';
import { ILightWeightObjectCollection } from '@fi-shared/models';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { Subject } from 'rxjs';
import { LinkToTypesService } from '@fi-shared/link-to/link-to-types.service';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';
import { DocumentGeneratePreviewService } from '@fi-app/document/document-generate-preview/document-generate-preview.service';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { FiLocalSearchParameterModel } from '@fi-shared/fi-local-search2/fi-local-search-parameter.model';
import { IItemsListDataFetchResult, IItemsListParameterBase } from '@fi-shared/fi-items-list/fi-items-list';
import { IDocumentModel } from '@fi-app/document/document';
import { removeItem } from '@fi-shared/utils/remove-item';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { ILightWeightTag } from '@fi-shared/fi-tag2/fi-tag2';
import { FiPrintDownloadService } from '@fi-shared/fi-print-download/fi-print-download.service';

@Injectable()
export class DocumentService {
    previewImageGenerated$ = new Subject<number>();

    constructor(
        private fiBackend: FiBackendService,
        private appText: AppTextService,
        private fiPrintDownloadService: FiPrintDownloadService,
        private fiSignalRHelperService: FiSignalRHelperService,
        private linkToTypesService: LinkToTypesService,
        private fileUpload2Service: FiFileUpload2Service,
        private fiLoading: LoadingService,
        private documentGeneratePreviewService: DocumentGeneratePreviewService,
        private injector: Injector,
    ) {
        this.fiSignalRHelperService.initialDraftDocumentsCount.subscribe((v) =>
            this.onDraftDocumentsCountChange.next(v),
        );
    }

    GetDocument(documentId: number, isViewMode?: boolean) {
        return new Promise<DocumentModel>((resolve, reject) => {
            if (documentId > 0) {
                /*let url2 = `api2/Document/GetDocument/${documentId}`; // todo for flutter app tests
               this.fiBackend.fetchData<any[]>(url2).then(data => {
                   console.log('api2 document', data);
               });*/

                this.fiBackend
                    .fetchData<IDocumentModel>(`Document/Document/GetDocument/${documentId}/${!!isViewMode}`)
                    .then(
                        (data) => {
                            resolve(new DocumentModel(data, false, this.injector));
                        },
                        () => reject(),
                    );
            } else {
                const document = new DocumentModel(undefined, undefined, this.injector);
                document.Initialize(undefined, undefined, this.injector);
                resolve(document);
            }
        });
    }

    GetDocumentCopy(documentId: number, selectedLanguageId: number) {
        const self = this;
        const promise = new Promise<DocumentModel>((resolve, reject) => {
            this.fiBackend
                .fetchData<IDocumentModel>(`Document/Document/GetDocumentCopy/${documentId}/${selectedLanguageId}`)
                .then(
                    (data) => {
                        const document = new DocumentModel(data, false, this.injector);
                        document.Name = getCopyOf(document.Name);
                        document.Description = getCopyOf(document.Description);
                        document.Names.forEach((item) => (item.Value = getCopyOf(item.Value)));
                        document.Descriptions.forEach((item) => (item.Value = getCopyOf(item.Value)));
                        resolve(document);
                    },
                    () => {
                        reject();
                    },
                );
        });
        return promise;

        function getCopyOf(value) {
            return value ? self.appText.get('copy_of') + ' ' + value : value;
        }
    }

    GetDocumentControlContentsVersion(DocumentId: number, DocumentVersion: number, ContentLanguageId: number) {
        return this.fiBackend
            .fetchData<
                any[]
            >(`Document/Document/GetDocumentControlContentsVersion/${DocumentId}/${DocumentVersion}/${ContentLanguageId}`)
            .then((data) => data.map((item) => new DocumentControlContentLanguageLabelModel(item)));
    }

    GetDocumentTags() {
        return this.fiBackend.fetchData<ILightWeightTag[]>('Document/Document/GetDocumentTags');
    }

    GetDocumentTagsByUserAccessRights(): Promise<ILightWeightTag[]> {
        return this.fiBackend.fetchData<ILightWeightTag[]>(
            `Core/Tag/GetModuleTagsByUserAccessRights/${ModuleType.Document}`,
        );
    }

    private documentsDeleteInProgress = [];

    deleteDocuments(documentIdsToDelete: number[], showLoading: boolean = false): Promise<void> {
        this.documentsDeleteInProgress.push(...documentIdsToDelete);
        let promise = this.fiBackend.fetchDataPost<void>('Document/Document/DeleteDocuments', documentIdsToDelete);
        if (showLoading) {
            promise = this.fiLoading.trackPromise(promise);
        }
        promise.finally(() => {
            documentIdsToDelete.forEach((id) => {
                removeItem(this.documentsDeleteInProgress, id);
            });
        });
        return promise;
    }

    IsDocumentInDeleteProgress(documentId) {
        return this.documentsDeleteInProgress.indexOf(documentId) > -1;
    }

    deleteDocumentVersions(documentVersionIds: number[]) {
        return this.fiBackend.fetchDataPost<void>(
            'Document/Document/DeleteDocumentControlContentVersions',
            documentVersionIds,
        );
    }

    SaveDocument(documentToSave: DocumentModel, contentLanguageId: number) {
        this.linkToTypesService.invalidateDocuments(); // invalidate saved lightweight documents
        documentToSave.DocumentControlContents.forEach(function (controlContent) {
            if (isObject(controlContent.Value)) controlContent.Value = JSON.stringify(controlContent.Value);
        });
        const promise = this.fiBackend.fetchDataPost<number>('Document/Document/SaveDocument', documentToSave);
        promise.then((documentId) => {
            documentToSave.Id = documentId;
            if (!documentToSave.IsDraft && this.hasVimeoFilesInProcessing(documentToSave)) {
                // we should wait until vimeo files will be processed and then regenerate preview
                //this.checkIfShouldRegenerateAfterVimeoFinishesProcessing();
                const _s = this.fileUpload2Service.vimeoFilesInProcessChanged$.subscribe((x) => {
                    if (!this.hasVimeoFilesInProcessing(documentToSave)) {
                        _s.unsubscribe();
                        this.GetDocument(documentId, true).then((documentForPreview) => {
                            this.documentGeneratePreviewService.generateDocumentPreviewImage(
                                documentForPreview,
                                contentLanguageId,
                            );
                        });
                    }
                });
            }
        });
        return promise;
    }

    private hasVimeoFilesInProcessing(document: DocumentModel) {
        //const mediaBankFileIds = document.MediaBankFileReferences.map(item => item.MediaBankFileId);
        const vimeoFilesInProcess = this.fileUpload2Service.getVimeoFilesInProcess();
        return !!vimeoFilesInProcess.length;
        //return !!vimeoFilesInProcess.find(item => mediaBankFileIds.indexOf(item.MediaBankFileId) !== -1);
    }

    getDocumentsListData(parameter: FiLocalSearchParameterModel | IItemsListParameterBase) {
        return this.fiBackend.fetchDataPost<IItemsListDataFetchResult<IDocumentModel>>(
            'Document/Document/GetDocumentsListData',
            parameter,
        );
    }

    GetDocuments(documentParameterApiModel) {
        return this.fiBackend.fetchDataPost<DocumentModel[]>(
            'Document/Document/GetDocuments',
            documentParameterApiModel,
        );
    }

    GetDocumentsCount(documentParameterApiModel) {
        return this.fiBackend.fetchDataPost<number>('Document/Document/GetDocumentsCount', documentParameterApiModel);
    }

    onDraftDocumentsCountChange = new Subject<number>();
    getDraftDocuments() {
        return this.fiBackend.fetchData<IDocumentModel[]>('Document/Document/GetDraftDocuments');
    }

    GetLightWeightObjectsForLocalSearch(documentParameterApiModel) {
        return this.fiBackend.fetchDataPost<ILightWeightObjectCollection>(
            'Document/Document/GetLightWeightObjectsForLocalSearch',
            documentParameterApiModel,
        );
    }

    GenerateDocumentPreviewFileImage(DocumentId: number, Source: string) {
        const promise = this.fiPrintDownloadService.generatePreviewFileImage(ObjectTypes.Document, DocumentId, Source);
        promise.then(() => this.previewImageGenerated$.next(DocumentId));
        return promise;
    }

    RestoreDocuments(restoreIds: number[]) {
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchDataPost<void>('Document/Document/RestoreDocuments', restoreIds),
        );
    }

    MarkDocumentAsVisited(id: number) {
        return this.fiBackend.fetchDataPost('Document/Document/MarkDocumentAsVisited/' + id, undefined, {
            supressErrorMessage: true,
        });
    }

    markAllDocumentsAsSeen() {
        return this.fiBackend.fetchDataPost('Document/Document/MarkAllDocumentsAsSeen');
    }

    SaveSettingAsDefault(documentDefaultSettingModel) {
        return this.fiBackend.fetchDataPost('Document/Document/SaveSettingAsDefault', documentDefaultSettingModel);
    }

    GetDefaultSetting() {
        return this.fiBackend.fetchData('Document/Document/GetDefaultSetting');
    }

    SaveTemplateAsDefault(DocumentDefaultTemplateModel) {
        return this.fiBackend.fetchDataPost('Document/Document/SaveTemplateAsDefault', DocumentDefaultTemplateModel);
    }

    GetDefaultTemplate() {
        return this.fiBackend.fetchData('Document/Document/GetDefaultTemplate');
    }

    GetDocumentByVersion(documentId, version) {
        return new Promise<DocumentModel>((resolve, reject) => {
            this.fiBackend
                .fetchData<IDocumentModel>(`Document/Document/GetDocumentByVersion/${documentId}/${version}`)
                .then((data) => {
                    const document = new DocumentModel(data, false, this.injector);
                    resolve(document);
                }, reject);
        });
    }

    private _autoSavedDocumentRemoved$ = new ReplaySubject<IAutoSavedDocumentRemovedData>(1);
    get autoSavedDocumentRemoved$(): Observable<IAutoSavedDocumentRemovedData> {
        return this._autoSavedDocumentRemoved$.asObservable();
    }
    raiseAutoSavedDocumentRemoved(data: IAutoSavedDocumentRemovedData) {
        this._autoSavedDocumentRemoved$.next(data);
    }

    initForSystemNotifications() {
        // todo
    }

    getDocumentsForExport(): Promise<DocumentModel[]> {
        const promise = new Promise<DocumentModel[]>((resolve, reject) => {
            this.fiBackend.fetchData<IDocumentModel[]>('Document/Document/GetDocumentsForExport').then((result) => {
                const documents = result.map((doc) => new DocumentModel(doc, undefined, this.injector));
                resolve(documents);
            }, reject);
        });
        return this.fiLoading.trackPromise(promise);
    }

    private _reloadDocumentList$ = new Subject<void>();
    doReloadDocumentList$() {
        this._reloadDocumentList$.next();
    }

    get reloadDocumentList$(): Observable<any> {
        return this._reloadDocumentList$.asObservable();
    }
}

interface IAutoSavedDocumentRemovedData {
    OriginalId: number;
}
